import React, { useMemo } from "react";

import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { assertNever } from "@impero-com/common/utils/obj";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import {
    DATAGRID_MAX_NUMBER_OF_COLUMNS,
    DATAGRID_MAX_NUMBER_OF_ROWS,
} from "components/fieldBuilder/consts";
import { DataGridEditor } from "components/fieldBuilder/dataGrid";
import { DataSheetEditor } from "components/fieldBuilder/dataSheet";
import { EmptyCustomSection } from "components/fieldBuilder/emptyCustomSection";
import { CompoundFormForwardedRef } from "components/fieldBuilder/fieldBuilder";
import { FieldDrawerSeparator } from "components/fieldBuilder/fieldDrawerSeparator";
import { FieldOptionsEditor } from "components/fieldBuilder/options";

import {
    ControlField,
    ControlFieldType,
    MoneyInputFieldType,
} from "./fieldBuilderUtils";
import { MoneyInputEditor } from "./MoneyInputEditor";
import { DataSheetAssignmentsForControlProgramAndCurrentControl } from "./types";

interface FieldCustomSectionProps {
    fieldAtom: FormAtom<ControlField>;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    controlOverrideId: string;
    controlOverrideTitle: string;
    disabled?: boolean;
}

export const FieldCustomSection = React.forwardRef(
    (
        {
            fieldAtom,
            dataSheetAssignments,
            controlOverrideId,
            controlOverrideTitle,
            disabled = false,
        }: FieldCustomSectionProps,
        ref: CompoundFormForwardedRef
    ) => {
        const { value: field } = useFormAtom(fieldAtom);
        const fieldTypeAtom = useMemo(
            () =>
                formAtom(
                    focusAtom(fieldAtom, (fa) => fa.prop("fieldType")),
                    Yup.mixed<ControlFieldType>().defined()
                ),
            [fieldAtom]
        );
        switch (field.fieldType.type) {
            case "FileUpload":
            case "NumericInput":
            case "TextInput":
            case "TextArea":
            case "Heading": {
                return <EmptyCustomSection ref={ref} />;
            }
            case "MoneyInput": {
                const fta = fieldTypeAtom as FormAtom<MoneyInputFieldType>;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <MoneyInputEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "CheckboxGroup": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ControlFieldType, { type: "CheckboxGroup" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "RadioButtonGroup": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ControlFieldType, { type: "RadioButtonGroup" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "Dropdown": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ControlFieldType, { type: "Dropdown" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <FieldOptionsEditor
                            ref={ref}
                            atom={fta}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "DataGrid": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ControlFieldType, { type: "DataGrid" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <DataGridEditor
                            ref={ref}
                            atom={fta}
                            maxNumberOfColumns={DATAGRID_MAX_NUMBER_OF_COLUMNS}
                            maxNumberOfRows={DATAGRID_MAX_NUMBER_OF_ROWS}
                            disabled={disabled}
                        />
                    </>
                );
            }
            case "DataSheet": {
                const fta = fieldTypeAtom as FormAtom<
                    Extract<ControlFieldType, { type: "DataSheet" }>
                >;
                return (
                    <>
                        <FieldDrawerSeparator />
                        <DataSheetEditor
                            ref={ref}
                            fieldId={field.id}
                            atom={fta}
                            dataSheetAssignments={dataSheetAssignments}
                            controlOverrideId={controlOverrideId}
                            controlOverrideTitle={controlOverrideTitle}
                            disabled={disabled}
                        />
                    </>
                );
            }
            default: {
                return assertNever(field.fieldType);
            }
        }
    }
);
